"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Loader2 } from "@/components/icons";
import { useZipGeocoding, type ZipLocation } from "@/hooks/use-zip-geocoding";
import { normalizeZipInput } from "@/lib/validation/core/primitives";

export interface ZipInputProps {
 value: string;
 onChange: (zip: string) => void;
 onGeocode?: (result: ZipLocation | null) => void;
 placeholder?: string;
 compact?: boolean;
 disabled?: boolean;
 className?: string;
 inputClassName?: string;
 id?: string;
 "aria-invalid"?: boolean;
 "aria-describedby"?: string;
}

function useGeocodeCompleteZip(
  value: string,
  onGeocode: ZipInputProps["onGeocode"],
) {
  const { isLoading, error, geocodeZip, clearLocation } = useZipGeocoding();
  const lastGeocodedRef = useRef<string | null>(null);

  useEffect(() => {
    const normalizedValue = value.trim();

    if (normalizedValue.length === 5 && /^\d{5}$/.test(normalizedValue)) {
      if (lastGeocodedRef.current !== normalizedValue) {
        lastGeocodedRef.current = normalizedValue;
        geocodeZip(normalizedValue).then((result) => {
          onGeocode?.(result);
        });
      }
    } else {
      if (lastGeocodedRef.current !== null) {
        lastGeocodedRef.current = null;
        clearLocation();
        onGeocode?.(null);
      }
    }
  }, [value, geocodeZip, clearLocation, onGeocode]);

  return { isLoading, error };
}

export function ZipInput({
  value,
  onChange,
  onGeocode,
  placeholder = "ZIP",
  compact = false,
  disabled = false,
  className,
  inputClassName,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedby,
}: ZipInputProps) {
  const { isLoading, error } = useGeocodeCompleteZip(value, onGeocode);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(normalizeZipInput(event.target.value));
  };

  const showError = error && !isLoading;

  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={cn(
          showError && "border-destructive focus-visible:ring-destructive",
          inputClassName,
        )}
        disabled={disabled}
        autoComplete="postal-code"
        inputMode="numeric"
        maxLength={5}
        aria-invalid={ariaInvalid || showError || undefined}
        aria-describedby={ariaDescribedby}
        size={compact ? "compact" : "default"}
      />
      {isLoading && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
